// PlayerProfile.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FlagDisplay from '../components/FlagDisplay'; 
import {
  Tabs,
  Tooltip,
  Tab,
  Box,
  Avatar,
  Typography,
  Chip,
  Paper,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Grid,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  FormGroup,
  FormControlLabel,
  TextField,
  Card,
  CardContent
} from '@mui/material';

import {
  ArrowBack,
  Edit as EditIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  PhotoCamera,
  Image as ImageIcon,
  Edit,
  OpenInNew,
  PictureAsPdf as PictureAsPdfIcon,
  Healing as HealingIcon,
  Gavel as GavelIcon,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Visibility,
  Cancel,
  TrackChanges,
  Flag,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';

import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';

import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import InformeTab from '../components/InformeTab';
import IntelTab from '../components/IntelTab';
import TechnicaTab from '../components/TechnicaTab';
import PDPTab from '../components/PDPTab';
import VideoTab from '../components/VideoTab'; 
import NewsTab from '../components/NewsTab';
import MedicalTab from '../components/MedicalTab';
import MedidasTab from '../components/MedidasTab';
import DocumentsTab from '../components/DocumentsTab';
import CanteraStatsTab from '../components/CanteraStatsTab';

import EstadisticasTab from '../components/EstadisticasTab';
import ShootingBadges from '../components/ShootingBadges';
import ProfilScheduleTab from '../components/ProfilScheduleTab';
import InstatPlayerView from './Instat/InstatPlayerView';

import InformacionBasica from '../components/profile/InformacionBasica';
import CaracteristicasJugador from '../components/profile/CaracteristicasJugador';
import SituacionActual from '../components/profile/SituacionActual';
import ExperienciaProfesional from '../components/profile/ExperienciaProfesional';
import InformacionContractual from '../components/profile/InformacionContractual';
import InformacionAdicional from '../components/profile/InformacionAdicional';
import DefensiveVersatilitySection from '../components/profile/DefensiveVersatilitySection';
import { normalizeVersatility } from '../utils/versatility';

import { generatePDF } from './PlayerProfilePdf';

const resizeImageFile = (file, maxDimension = 800) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxDimension) {
          height = height * (maxDimension / width);
          width = maxDimension;
        } else if (height > maxDimension) {
          width = width * (maxDimension / height);
          height = maxDimension;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            const resizedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(resizedFile);
          },
          'image/jpeg',
          0.8
        );
      };
      img.onerror = reject;
      img.src = event.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const formatDateForBackend = (input) => {
  if (!input || input.includes('T')) return input;
  const [year, month, day] = input.split('-');
  if (year && month && day) {
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return input;
};

const FormIndicator = ({ forme, size = 24 }) => {
  const { t } = useTranslation();
  if (!forme) return null;

  const getFormIcon = () => {
    switch (forme.toLowerCase()) {
      case 'high':
        return <TrendingUp sx={{ color: '#4caf50', fontSize: size }} />;
      case 'low':
        return <TrendingDown sx={{ color: '#f44336', fontSize: size }} />;
      case 'stable':
        return <TrendingFlat sx={{ color: '#ff9800', fontSize: size }} />;
      default:
        return null;
    }
  };

  const getFormLabel = () => {
    switch (forme.toLowerCase()) {
      case 'high':
        return t('playerProfile.form.high');
      case 'low':
        return t('playerProfile.form.low');
      case 'stable':
        return t('playerProfile.form.stable');
      default:
        return '';
    }
  };

  return (
    <Tooltip title={getFormLabel()} placement="top" arrow>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {getFormIcon()}
      </Box>
    </Tooltip>
  );
};

// Composant ScoutVerdicts modifié pour PlayerProfile.js avec correction
const ScoutVerdicts = ({ playerId }) => {
  const { t } = useTranslation();
  const [verdicts, setVerdicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVerdict, setSelectedVerdict] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentUserVerdict, setCurrentUserVerdict] = useState(null);
  const [newVerdict, setNewVerdict] = useState('');
  const [saving, setSaving] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setUserRole(user?.role?.toLowerCase());
    setCurrentUserId(user?.id);
  }, []);

  useEffect(() => {
    const fetchScoutVerdicts = async () => {
      try {
        const response = await api.get(
          `/api/players/${playerId}/scout-verdicts`
        );
        
        // Filtrer les verdicts selon le rôle
        let filteredVerdicts = response.data.verdicts;
        if (userRole === 'entrenador' && currentUserId) {
          filteredVerdicts = response.data.verdicts.filter(verdict => verdict.user_id === currentUserId);
        }
        
        setVerdicts(filteredVerdicts);
        
        // Trouver le verdict de l'utilisateur connecté
        const userVerdict = response.data.verdicts.find(v => v.user_id === currentUserId);
        setCurrentUserVerdict(userVerdict);
        setNewVerdict(userVerdict?.verdict || '');
      } catch (error) {
        console.error('Error fetching scout verdicts:', error);
      } finally {
        setLoading(false);
      }
    };

    if (userRole !== null && currentUserId) {
      fetchScoutVerdicts();
    }
  }, [playerId, userRole, currentUserId]);

  const verdictConfig = {
    'DESCARTADO': { color: '#f44336', emoji: '❌', label: t('playerProfile.verdicts.discarded') },
    'VOLVER A VER': { color: '#2196f3', emoji: '🔄', label: t('playerProfile.verdicts.reviewAgain') },
    'MONITORIZAR': { color: '#ff9800', emoji: '👁️', label: t('playerProfile.verdicts.monitor') },
    'PRIORITARIO': { color: '#4caf50', emoji: '⭐', label: t('playerProfile.verdicts.priority') },
    'DUDAS': { color: '#9c27b0', emoji: '❓', label: t('playerProfile.verdicts.dudas') }
  };

  const handleVerdictClick = (verdictType) => {
    const verdictsList = verdicts.filter(v => v.verdict === verdictType);
    setSelectedVerdict({ type: verdictType, verdicts: verdictsList });
    setDialogOpen(true);
  };

  const handleOpenEditDialog = () => {
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setNewVerdict(currentUserVerdict?.verdict || '');
  };

  const handleSaveVerdict = async () => {
    if (!newVerdict || !currentUserId) return;
    
    setSaving(true);
    try {
      const response = await api.post(
        `/api/players/${playerId}/scout-verdicts`,
        {
          verdict: newVerdict,
          userId: currentUserId
        }
      );

      // Actualiser les verdicts
      const updatedResponse = await api.get(
        `/api/players/${playerId}/scout-verdicts`
      );
      
      let filteredVerdicts = updatedResponse.data.verdicts;
      if (userRole === 'entrenador' && currentUserId) {
        filteredVerdicts = updatedResponse.data.verdicts.filter(verdict => verdict.user_id === currentUserId);
      }
      
      setVerdicts(filteredVerdicts);
      
      const userVerdict = updatedResponse.data.verdicts.find(v => v.user_id === currentUserId);
      setCurrentUserVerdict(userVerdict);
      
      setEditDialogOpen(false);
      
      // Afficher un message de succès
      console.log(response.data.message);
      
    } catch (error) {
      console.error('Error saving verdict:', error);
      // Ici vous pouvez ajouter une notification d'erreur
    } finally {
      setSaving(false);
    }
  };

  // CORRECTION : Condition simplifiée pour le loading
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // CORRECTION : Affichage du bouton "Añadir verdict" même quand il n'y a pas de verdicts
  if (verdicts.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('playerProfile.verdicts.noVerdict')}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={handleOpenEditDialog}
          startIcon={<Edit />}
          sx={{ fontSize: '0.75rem', padding: '4px 8px' }}
        >
          {t('playerProfile.verdicts.addVerdict')}
        </Button>

        {/* Dialog pour modifier le verdict */}
        <Dialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('playerProfile.verdicts.addVerdictTitle')}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t('playerProfile.verdicts.verdictLabel')}</InputLabel>
                <Select
                  value={newVerdict}
                  onChange={(e) => setNewVerdict(e.target.value)}
                  label={t('playerProfile.verdicts.verdictLabel')}
                >
                  {Object.entries(verdictConfig).map(([key, config]) => (
                    <MenuItem key={key} value={key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ fontSize: '16px' }}>
                          {config.emoji}
                        </Box>
                        {config.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSaveVerdict}
              variant="contained"
              disabled={saving || !newVerdict}
            >
              {saving ? <CircularProgress size={20} /> : t('common.save')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Grouper les verdicts par type
  const verdictGroups = verdicts.reduce((acc, verdict) => {
    if (!acc[verdict.verdict]) {
      acc[verdict.verdict] = [];
    }
    acc[verdict.verdict].push(verdict);
    return acc;
  }, {});

  const verdictSize = 28;
  const countBadgeSize = 20;
  const emojiSize = '32px';

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', mb: 1.5 }}>
          {Object.entries(verdictGroups).map(([verdictType, verdictsList]) => {
            const config = verdictConfig[verdictType];
            const count = verdictsList.length;
            const userNames = verdictsList.map(v => v.user_nom).join(', ');

            const verdictElement = (
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-block',
                  cursor: 'pointer'
                }}
                onClick={() => handleVerdictClick(verdictType)}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: emojiSize,
                    '&:hover': !isMobile ? {
                      transform: 'scale(1.15)',
                      transition: 'transform 0.2s'
                    } : {}
                  }}
                >
                  {config.emoji}
                </Box>
                {count > 1 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: -10,
                      backgroundColor: '#fff',
                      color: config.color,
                      borderRadius: '50%',
                      width: countBadgeSize,
                      height: countBadgeSize,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 'bold',
                      border: `2px solid ${config.color}`,
                    }}
                  >
                    {count}
                  </Box>
                )}
              </Box>
            );

            if (isMobile) {
              return <Box key={verdictType}>{verdictElement}</Box>;
            } else {
              return (
                <Tooltip
                  key={verdictType}
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{verdictType}</Typography>
                      <Typography variant="body2">{userNames}</Typography>
                    </Box>
                  }
                >
                  {verdictElement}
                </Tooltip>
              );
            }
          })}
        </Box>

        {/* Bouton pour modifier le verdict de l'utilisateur connecté */}
        <Button
          variant="outlined"
          size="small"
          onClick={handleOpenEditDialog}
          startIcon={<Edit />}
          sx={{ fontSize: '0.75rem', padding: '4px 10px' }}
        >
          {currentUserVerdict ? t('playerProfile.verdicts.modify') : t('playerProfile.verdicts.add')}
        </Button>
      </Box>

      {/* Dialog pour afficher les détails */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{selectedVerdict?.type}</DialogTitle>
        <DialogContent>
          <List>
            {selectedVerdict?.verdicts.map((verdict, index) => (
              <ListItem key={index}>
                <ListItemText
                  primary={verdict.user_nom}
                  secondary={new Date(verdict.created_at).toLocaleDateString('es-ES')}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour modifier le verdict */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {currentUserVerdict ? t('playerProfile.verdicts.modifyVerdictTitle') : t('playerProfile.verdicts.addVerdictTitle')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>{t('playerProfile.verdicts.verdictLabel')}</InputLabel>
              <Select
                value={newVerdict}
                onChange={(e) => setNewVerdict(e.target.value)}
                label={t('playerProfile.verdicts.verdictLabel')}
              >
                {Object.entries(verdictConfig).map(([key, config]) => (
                  <MenuItem key={key} value={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ fontSize: '16px' }}>
                        {config.emoji}
                      </Box>
                      {config.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveVerdict}
            variant="contained"
            disabled={saving || !newVerdict}
          >
            {saving ? <CircularProgress size={20} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const PlayerProfile = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const mediaUrl = getMediaBaseUrl();
  const section = process.env.REACT_APP_SECTION;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [player, setPlayer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editablePlayer, setEditablePlayer] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImagePreview, setSelectedImagePreview] = useState(null);
  const [allGroups, setAllGroups] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [avatarTimestamp, setAvatarTimestamp] = useState(Date.now());
  const [openPhotoDialog, setOpenPhotoDialog] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [hasInformeData, setHasInformeData] = useState(false);
  const [hasIntelData, setHasIntelData] = useState(false);
  const [hasTechnicaData, setHasTechnicaData] = useState(false);
  const [hasPDPData, setHasPDPData] = useState(false);
  const [hasVideoData, setHasVideoData] = useState(false);
  const [hasNewsData, setHasNewsData] = useState(false);

  const [hasStats, setHasStats] = useState(false);
  const [hasSchedule, setHasSchedule] = useState(false);

  const [hasMedicalData, setHasMedicalData] = useState(false);
  const [hasDocumentsData, setHasDocumentsData] = useState(false);

  const [medicalData, setMedicalData] = useState([]);

  const [isInjured, setIsInjured] = useState(false);

  const [latestMedical, setLatestMedical] = useState(null);

  const [informeList, setInformeList] = useState([]);
  const [intelList, setIntelList] = useState([]);

  const [teams, setTeams] = useState([]);
  const [leagues2, setLeagues2] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [userRole, setUserRole] = useState(null);

  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [checkedInformes, setCheckedInformes] = useState([]);
  const [checkedIntels, setCheckedIntels] = useState([]);
  const [masterCheckInformes, setMasterCheckInformes] = useState(false);
  const [masterCheckIntels, setMasterCheckIntels] = useState(false);

  const [includeProfileInfo, setIncludeProfileInfo] = useState(false);
  const [includeCaracteristicas, setIncludeCaracteristicas] = useState(false);
  const [includeExperiencia, setIncludeExperiencia] = useState(false);
  const [includeContrato, setIncludeContrato] = useState(false);
  const [selectAllSections, setSelectAllSections] = useState(false);

  const priceRanges = [
    '0-50k€', '50-100k€', '100-150k€', '150-200k€', '200-250k€',
    '250-300k€', '300-350k€', '350-400k€', '400-450k€', '450-500k€',
    '500-550k€', '550-600k€', '600-650k€', '650-700k€', '700-750k€',
    '750-800k€', '800-850k€', '850-900k€', '900-950k€', '950k€-1M€',
    '1M€-1.2M€', '1.2M€-1.4M€', '1.4M€-1.6M€', '1.6M€-1.8M€',
    '1.8M€-2M€', '2M€-2.2M€', '2.2M€-2.4M€', '2.4M€-2.5M€'
  ];
  const buyoutOptions = [...priceRanges];
  const contratos = [
    '2-Way', 'Exhibit-10', '1 año', '1+1', '2 años', '3 años', 'On Loan'
  ];

  const [openCreateFichaDialog, setOpenCreateFichaDialog] = useState(false);
  const [selectedFichaType, setSelectedFichaType] = useState('');

  const [hasMedidasData, setHasMedidasData] = useState(false);
  const [hasCanteraStats, setHasCanteraStats] = useState(false);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [wingspanDiff, setWingspanDiff] = useState(null);
  const [winPercentage, setWinPercentage] = useState(null);
  const [acbExperience, setAcbExperience] = useState(null);
  const [shootingSummary, setShootingSummary] = useState(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setUserRole(user?.role?.toLowerCase());
  }, []);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await api.get('/api/players/teams');
        setTeams(response.data.teams || []);
      } catch (error) {
        console.error('Error fetching teams:', error);
      }
    };
    const fetchLeagues2 = async () => {
      try {
        const response = await api.get('/api/players/leagues2');
        setLeagues2(response.data.leagues2 || []);
      } catch (error) {
        console.error('Error fetching leagues2:', error);
      }
    };
    const fetchAgencies = async () => {
      try {
        const response = await api.get('/api/players/agencies');
        setAgencies(response.data.agencies || []);
      } catch (error) {
        console.error('Error fetching agencies:', error);
      }
    };
    fetchTeams();
    fetchLeagues2();
    fetchAgencies();
  }, []);

  useEffect(() => {
    const fetchPlayer = async () => {
      try {
        setIsLoading(true);
        const response = await api.get(`/api/players/${id}`);
        const mappedPlayer = {
          ...response.data,
          dateNaissance: response.data.date_naissance,
          nacionalidad: response.data.nationalite,
          segundaNacionalidad: response.data.nationalite_2,
          seleccionNacional: response.data.selection_national,
          peso: response.data.poids,
          rolOfensivo: response.data.offensive_role,
          rolDefensivo: response.data.defensive_role,
          pasaporte: response.data.passport,
          estatus: response.data.statut,
          equipo: response.data.team,
          liga2: response.data.league_2,
          equipoPreDraft: response.data.pre_draft_team,
          añoDraft: response.data.draft_year,
          posicionDraft: response.data.draft_pick,
          experienciaAcb: response.data.acb_experience,
          nbaExperience: response.data.nba_experience,
          agencia: response.data.agency,
          comparacionPro: response.data.pro_comparaison,
          comportamiento: response.data.flag_behaviour,
          rangoPrecios: response.data.price_range,
          contrato: response.data.contract,
          nivel: response.data.level,
          proyeccion: response.data.proyeccion,
          jeJiType: response.data.je_ji || '',
          jeJiAttributes: response.data.selection_je_ji ? response.data.selection_je_ji.split(',') : [],
          pit: response.data.pit,
          eurocamp: response.data.eurocamp,
          injury: response.data.injury,
          digital_behaviour: response.data.digital_behaviour,
          experiencaEuropa: response.data.experienca_europa,
          shootingRange: response.data.shooting_range,
          origen_genetico: response.data.origen_genetico,
          categoria: response.data.categoria,
          forme_indicator: response.data.forme_indicator,
          competitions_detail: response.data.competitions_detail || [],
          defensive_versatility: normalizeVersatility(response.data.defensive_versatility)
        };
        setPlayer(mappedPlayer);
        setEditablePlayer(mappedPlayer);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.message || 'Error al cargar los datos del jugador');
        console.error('Error fetching player:', err);
      } finally {
        setIsLoading(false);
      }
    };
    if (id) fetchPlayer();
  }, [id]);

  useEffect(() => {
    const fetchExtraData = async () => {
      if (!player) return;
      try {
        const informeRes = await api.get('/api/informe', { params: { playerId: player.id } });
        setHasInformeData(informeRes.data.length > 0);
        setInformeList(informeRes.data);

        const intelRes = await api.get('/api/intel', { params: { playerId: player.id } });
        setHasIntelData(intelRes.data.length > 0);
        setIntelList(intelRes.data);

        const technicaRes = await api.get('/api/technique', { params: { playerId: player.id } });
        setHasTechnicaData(technicaRes.data.length > 0);

        const pdpRes = await api.get(`/api/tecnification/player/${player.id}/fiches`);
        setHasPDPData(pdpRes.data.length > 0);

        const videosRes = await api.get('/api/videos', { params: { playerId: player.id } });
        setHasVideoData(videosRes.data.length > 0);

        const newsHasRes = await api.get(`/api/news/player/${player.id}/has`);
        setHasNewsData(newsHasRes.data.hasNews === true);

        const medicalRes = await api.get('/api/medical', { params: { playerId: player.id } });
        setMedicalData(medicalRes.data);
        if (medicalRes.data.length > 0) {
          setHasMedicalData(true);
        }

        const documentsRes = await api.get('/api/documents', { params: { playerId: player.id } });
        setHasDocumentsData(documentsRes.data.length > 0);
      } catch (error) {
        console.error('Error al recuperar datos extra:', error);
      }
    };
    fetchExtraData();
  }, [player]);

  useEffect(() => {
    const fetchLatestMedical = async () => {
      if (!player) return;
      try {
        const resp = await api.get('/api/medical/latest', {
          params: { playerId: player.id }
        });
        if (resp.data) {
          setHasMedicalData(true);
          setLatestMedical(resp.data);

          const dateReprise = resp.data.date_reprise ? new Date(resp.data.date_reprise) : null;
          const now = new Date();

          if (dateReprise && dateReprise > now) {
            setIsInjured(true);
          }
          else if (resp.data.affiche_logo_blessure) {
            setIsInjured(true);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération de la dernière fiche medical:', error);
      }
    };
    fetchLatestMedical();
  }, [player]);

  useEffect(() => {
    const checkStatsForPlayer = async () => {
      if (!id) return;
      try {
        const resp = await api.get(`/api/player-stats/${id}/exists`);
        if (resp.data.exists) {
          setHasStats(true);
        }
      } catch (error) {
        console.error('Error checking stats for player:', error);
      }
    };

    const checkScheduleForPlayer = async () => {
      if (!id) return;
      try {
        const resp = await api.get(`/api/player-stats/${id}/schedule-exists`);
        if (resp.data.exists) {
          setHasSchedule(true);
        }
      } catch (error) {
        console.error('Error checking schedule for player:', error);
      }
    };

    checkStatsForPlayer();
    checkScheduleForPlayer();
  }, [id]);

  useEffect(() => {
    const fetchGroups = async () => {
      if (player) {
        try {
          const response = await api.get('/api/groups');
          setAllGroups(response.data);
        } catch (error) {
          console.error('Error al recuperar grupos:', error);
        }
      }
    };
    fetchGroups();
  }, [player]);

  useEffect(() => {
    const checkMedidasForPlayer = async () => {
      if (!player) return;
      try {
        const resp = await api.get('/api/medidas/has', {
          params: { playerId: player.id }
        });
        if (resp.data.hasMedidas) {
          setHasMedidasData(true);
        }
      } catch (error) {
        console.error('Error checking medidas for player:', error);
      }
    };
    checkMedidasForPlayer();
  }, [player]);

  useEffect(() => {
    const fetchWingspanDiff = async () => {
      if (!player) return;
      try {
        const resp = await api.get('/api/medidas/wingspan-diff', {
          params: { playerId: player.id }
        });
        if (resp.data.diff !== null) {
          setWingspanDiff(resp.data.diff);
        }
      } catch (error) {
        console.error('Error fetching wingspan diff:', error);
      }
    };
    fetchWingspanDiff();
  }, [player]);

  useEffect(() => {
    const fetchWinPercentage = async () => {
      if (!id) return;
      try {
        const resp = await api.get(`/api/player-stats/${id}/win-percentage`);
        if (resp.data) {
          setWinPercentage(resp.data);
        }
      } catch (error) {
        console.error('Error fetching win percentage:', error);
      }
    };
    fetchWinPercentage();
  }, [id]);

  useEffect(() => {
    const fetchAcbExperience = async () => {
      if (!id) return;
      try {
        const resp = await api.get(`/api/player-stats/${id}/acb-experience`);
        if (resp.data && resp.data.seasons > 0) {
          setAcbExperience(resp.data);
        } else {
          setAcbExperience(null);
        }
      } catch (error) {
        console.error('Error fetching ACB experience:', error);
        setAcbExperience(null);
      }
    };
    fetchAcbExperience();
  }, [id]);

  useEffect(() => {
    if (!hasStats || !id) {
      setShootingSummary(null);
      return;
    }
    const fetchShootingSummary = async () => {
      try {
        const resp = await api.get(`/api/player-stats/${id}/shooting-summary`);
        if (resp.data && resp.data.current) {
          setShootingSummary(resp.data);
        } else {
          setShootingSummary(null);
        }
      } catch (error) {
        console.error('Error fetching shooting summary:', error);
        setShootingSummary(null);
      }
    };
    fetchShootingSummary();
  }, [hasStats, id]);

  useEffect(() => {
    const checkCanteraStatsForPlayer = async () => {
      if (!player) return;
      try {
        const resp = await api.get(`/api/stats/player-stats/${player.id}`);
        if (resp.data && resp.data.length > 0) {
          setHasCanteraStats(true);
        }
      } catch (error) {
        console.error('Error checking cantera stats for player:', error);
      }
    };
    checkCanteraStatsForPlayer();
  }, [player]);

  // Handle location state to set the active tab
  useEffect(() => {
    if (location.state?.activeTab) {
      const tabToActivate = location.state.activeTab;
      
      // Wait for sections to be loaded
      const timer = setTimeout(() => {
        const sections = [
          { id: 'info', available: true },
          { id: 'tecnica', available: hasTechnicaData },
          { id: 'informe', available: hasInformeData },
          { id: 'intel', available: hasIntelData },
          { id: 'medical', available: hasMedicalData },
          { id: 'medidas', available: hasMedidasData },
          { id: 'video', available: hasVideoData },
          { id: 'instat', available: !!player?.instat_player_id },
          { id: 'pdp', available: hasPDPData },
          { id: 'news', available: hasNewsData },
          { id: 'estadisticas', available: hasStats },
          { id: 'schedule', available: hasSchedule },
          { id: 'canterastats', available: hasCanteraStats }
        ].filter((s) => s.available);
        
        const tabIndex = sections.findIndex(s => s.id === tabToActivate);
        if (tabIndex !== -1) {
          setActiveTab(tabIndex);
        }
      }, 500); // Small delay to ensure data is loaded
      
      return () => clearTimeout(timer);
    }
  }, [location.state, hasInformeData, hasIntelData, hasTechnicaData, hasVideoData,
      hasPDPData, hasNewsData, hasStats, hasMedicalData, hasMedidasData, hasCanteraStats,
      player?.instat_player_id]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    setEditablePlayer((prev) => ({ ...prev, [name]: fieldValue }));
  };

  const handlePhotoCapture = (file) => {
    setSelectedImage(file);
    setSelectedImagePreview(URL.createObjectURL(file));
    setOpenPhotoDialog(false);
  };

  const handlePhotoSelect = (file) => {
    setSelectedImage(file);
    setSelectedImagePreview(URL.createObjectURL(file));
    setOpenPhotoDialog(false);
  };

  const handleGroupChange = (e, group) => {
    setEditablePlayer((prev) => {
      const isGroupSelected = prev.groupes?.some((g) => g.id === group.id);
      const updatedGroups = isGroupSelected
        ? prev.groupes.filter((g) => g.id !== group.id)
        : [...(prev.groupes || []), group];
      return { ...prev, groupes: updatedGroups };
    });
  };

  const handleUpdate = async () => {
    const dateNaissanceFormatted = formatDateForBackend(editablePlayer.dateNaissance);
    const anneeNaissanceFormatted = dateNaissanceFormatted ? new Date(dateNaissanceFormatted).getFullYear() : '';

    // Fonction helper pour traduire les valeurs avec clés de traduction
    const translateValue = (value, prefix) => {
      if (!value) return '';
      // Si la valeur est déjà une clé de traduction (format: key), on traduit
      // Sinon on garde la valeur telle quelle (anciennes données)
      const translated = t(`${prefix}.${value}`, { defaultValue: '' });
      return translated || value;
    };

    // Traduction des rôles offensifs/défensifs (peuvent être multiples séparés par virgule)
    const translateRoles = (roles, prefix) => {
      if (!roles) return '';
      return roles.split(',').map(role => {
        const trimmedRole = role.trim();
        const translated = t(`${prefix}.${trimmedRole}`, { defaultValue: '' });
        return translated || trimmedRole;
      }).join(',');
    };

    // Traduction des attributs JE/JI
    const translateJeJiAttributes = (attributes) => {
      if (!attributes || !attributes.length) return '';
      return attributes.map(attr => {
        const translated = t(`createPlayer.jeJiAttributes.${attr}`, { defaultValue: '' });
        return translated || attr;
      }).join(',');
    };

    const formData = new FormData();
    formData.append('nom', editablePlayer.nom || '');
    formData.append('type', editablePlayer.type || '');
    formData.append('annee_naissance', anneeNaissanceFormatted || '');
    formData.append('date_naissance', dateNaissanceFormatted || '');
    formData.append('taille', editablePlayer.altura || editablePlayer.taille || '');
    formData.append('poids', editablePlayer.peso || '');
    formData.append('poste', editablePlayer.poste || '');
    formData.append('nationalite', editablePlayer.nacionalidad || '');
    formData.append('nationalite_2', editablePlayer.segundaNacionalidad || '');
    formData.append('selection_national', editablePlayer.seleccionNacional || '');
    // Mano - traduit dans la langue du club
    formData.append('mano', translateValue(editablePlayer.mano, 'createPlayer.fields'));
    formData.append('passport', editablePlayer.pasaporte || '');
    formData.append('statut', editablePlayer.estatus || '');
    formData.append('team', editablePlayer.equipo || '');
    formData.append('league_2', editablePlayer.liga2 || '');
    formData.append('pre_draft_team', editablePlayer.equipoPreDraft || '');
    formData.append('draft_year', editablePlayer.añoDraft || '');
    formData.append('draft_pick', editablePlayer.posicionDraft || '');
    // Expériences - traduites dans la langue du club
    formData.append('acb_experience', translateValue(editablePlayer.experienciaAcb, 'common'));
    formData.append('nba_experience', translateValue(editablePlayer.nbaExperience, 'common'));
    // Niveau et projection - traduits dans la langue du club
    formData.append('level', translateValue(editablePlayer.nivel, 'createPlayer.levels'));
    formData.append('proyeccion', translateValue(editablePlayer.proyeccion, 'createPlayer.levels'));
    formData.append('agency', editablePlayer.agencia || '');
    formData.append('pro_comparaison', editablePlayer.comparacionPro || '');
    formData.append('flag_behaviour', editablePlayer.comportamiento || '');
    formData.append('injury', editablePlayer.injury || '');
    formData.append('digital_behaviour', editablePlayer.digital_behaviour || '');
    formData.append('price_range', editablePlayer.rangoPrecios || '');
    formData.append('contract', editablePlayer.contrato || '');
    formData.append('url', editablePlayer.url || '');
    formData.append('instat_url', editablePlayer.instat_url || '');
    formData.append('pit', editablePlayer.pit || '');
    formData.append('eurocamp', editablePlayer.eurocamp || '');
    formData.append('je_ji', editablePlayer.jeJiType || '');
    // Attributs JE/JI - traduits dans la langue du club
    const jeJiAttributesValue = translateJeJiAttributes(editablePlayer.jeJiAttributes);
    formData.append('selection_je_ji', jeJiAttributesValue);
    // Rôles offensifs/défensifs - traduits dans la langue du club
    formData.append('offensive_role', translateRoles(editablePlayer.rolOfensivo, 'createPlayer.offensiveRoles'));
    formData.append('defensive_role', translateRoles(editablePlayer.rolDefensivo, 'createPlayer.defensiveRoles'));
    if (editablePlayer.groupes) {
      formData.append('groupes', JSON.stringify(editablePlayer.groupes.map((g) => g.id)));
    }
    if (editablePlayer.competitions_detail) {
    formData.append('competitions_detail', JSON.stringify(editablePlayer.competitions_detail));
    }
    // Engagements au niveau joueur (source de vérité, indispensable pour un free
    // agent sans compétition). '' => le backend interprète comme null.
    formData.append('equipe_proprietaire_id', editablePlayer.equipe_proprietaire_id || '');
    formData.append('ncaa_commitment_id', editablePlayer.ncaa_commitment_id || '');
    formData.append('tanteo_id', editablePlayer.tanteo_id || '');
    if (editablePlayer.defensive_versatility !== undefined) {
      formData.append(
        'defensive_versatility',
        editablePlayer.defensive_versatility ? JSON.stringify(editablePlayer.defensive_versatility) : ''
      );
    }
    // Expériences additionnelles - traduites dans la langue du club
    formData.append('experienca_europa', translateValue(editablePlayer.experiencaEuropa, 'common'));
    formData.append('shooting_range', editablePlayer.shootingRange || '');
    formData.append('buyout', editablePlayer.buyout || '');
    // Origine génétique - traduit dans la langue du club
    formData.append('origen_genetico', translateValue(editablePlayer.origen_genetico, 'createPlayer.geneticOrigins'));
    formData.append('slvegas', editablePlayer.slvegas || '');
    formData.append('experienca_fuera', translateValue(editablePlayer.experienca_fuera, 'common'));
    formData.append('experienca_ncaa', translateValue(editablePlayer.experienca_ncaa, 'common'));
    formData.append('is_free_agent', editablePlayer.is_free_agent ? '1' : '0');
    // Catégorie - traduite dans la langue du club
    formData.append('categoria', translateValue(editablePlayer.categoria, 'createPlayer.categories'));

    if (selectedImage) {
      formData.append('avatar', selectedImage);
    }

    try {
      await api.put(
        `/api/players/${editablePlayer.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      // Mirror the backend's extraction of instat_player_id from instat_url
      // so the new Instat tab appears immediately without a page reload.
      const extractedInstatId = (() => {
        const raw = editablePlayer.instat_url;
        if (!raw) return null;
        const m = String(raw).match(/players\/(\d+)/);
        return m ? Number(m[1]) : null;
      })();
      setPlayer({
        ...editablePlayer,
        date_naissance: dateNaissanceFormatted,
        annee_naissance: anneeNaissanceFormatted,
        instat_player_id: extractedInstatId,
      });
      setIsEditing(false);
      setAvatarTimestamp(Date.now());
      if (isMobile) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Error al actualizar el jugador');
      console.error('Update error:', error);
    }
  };

  const handleOpenPdfDialog = () => {
    setSelectAllSections(false);
    setIncludeProfileInfo(false);
    setIncludeCaracteristicas(false);
    setIncludeExperiencia(false);
    setIncludeContrato(false);
    setCheckedInformes([]);
    setCheckedIntels([]);
    setMasterCheckInformes(false);
    setMasterCheckIntels(false);
    setPdfDialogOpen(true);
  };

  const handleClosePdfDialog = () => {
    setPdfDialogOpen(false);
  };

  const handleToggleInforme = (informe) => {
    setCheckedInformes((prev) => {
      const isChecked = prev.some((inf) => inf.id === informe.id);
      return isChecked ? prev.filter((inf) => inf.id !== informe.id) : [...prev, informe];
    });
  };

  const handleToggleIntel = (intel) => {
    setCheckedIntels((prev) => {
      const isChecked = prev.some((item) => item.id === intel.id);
      return isChecked ? prev.filter((item) => item.id !== intel.id) : [...prev, intel];
    });
  };

  const handleMasterCheckInformes = (event) => {
    const newChecked = event.target.checked;
    setMasterCheckInformes(newChecked);
    setCheckedInformes(newChecked ? informeList : []);
  };

  const handleMasterCheckIntels = (event) => {
    const newChecked = event.target.checked;
    setMasterCheckIntels(newChecked);
    setCheckedIntels(newChecked ? intelList : []);
  };

  const handleRefreshLatestMedical = async () => {
    try {
      const resp = await api.get('/api/medical/latest', {
        params: { playerId: player.id }
      });
      if (resp.data) {
        setLatestMedical(resp.data);
        const dateReprise = resp.data.date_reprise ? new Date(resp.data.date_reprise) : null;
        const now = new Date();
        if ((dateReprise && dateReprise > now) || resp.data.affiche_logo_blessure) {
          setIsInjured(true);
        } else {
          setIsInjured(false);
        }
      } else {
        setLatestMedical(null);
        setIsInjured(false);
      }
    } catch (err) {
      console.error('Erreur lors du rafraîchissement de la dernière fiche medical:', err);
    }
  };

  const handleGeneratePDF = async () => {
    handleClosePdfDialog();

    // Récupérer les infos du club depuis localStorage
    const user = JSON.parse(localStorage.getItem('user'));
    const clubTheme = JSON.parse(localStorage.getItem('clubTheme') || '{}');
    const clubInfo = {
      name: localStorage.getItem('clubName') || user?.club?.name || 'Club',
      slug: localStorage.getItem('clubSlug') || user?.club?.slug || 'default',
      primaryColor: clubTheme.primaryColor || user?.club?.color || '#EA7F00'
    };

    // Fonction pour traduire une valeur selon son type de champ
    // Gère les cas où la valeur est une clé de traduction OU une valeur déjà traduite
    const translateValue = (value, fieldType) => {
      if (!value) return '';

      // Mapping des types de champs vers les préfixes de traduction
      const prefixMap = {
        nationality: 'nationalities',
        geneticOrigin: 'createPlayer.geneticOrigins',
        nationalTeam: 'nationalTeams',
        hand: 'createPlayer.fields',
        offensiveRole: 'createPlayer.offensiveRoles',
        defensiveRole: 'createPlayer.defensiveRoles',
        level: 'createPlayer.levels',
        yesNo: 'common',
        flagBehaviour: 'createPlayer.fields', // Green, Orange, Red sont dans createPlayer.fields
        position: 'positions', // Base root level positions
        shootingRange: null // Pas de traduction pour shooting range (Rim, Mid-Range, etc.)
      };

      const prefix = prefixMap[fieldType];
      if (!prefix) return value; // Si pas de préfixe, retourner la valeur telle quelle

      // Fonction helper pour essayer de traduire une valeur unique
      const tryTranslate = (val) => {
        // Essayer d'abord avec la valeur telle quelle
        let translated = t(`${prefix}.${val}`, { defaultValue: '' });
        if (translated) return translated;

        // Essayer avec la première lettre en minuscule (Green -> green)
        const lowerFirst = val.charAt(0).toLowerCase() + val.slice(1);
        translated = t(`${prefix}.${lowerFirst}`, { defaultValue: '' });
        if (translated) return translated;

        // Essayer tout en minuscule
        translated = t(`${prefix}.${val.toLowerCase()}`, { defaultValue: '' });
        if (translated) return translated;

        // Retourner la valeur originale si aucune traduction trouvée
        return val;
      };

      // Si la valeur contient des virgules (multiples valeurs), les traiter séparément
      if (value.includes(',')) {
        return value.split(',').map(v => tryTranslate(v.trim())).join(', ');
      }

      return tryTranslate(value);
    };

    // Construire l'objet de traductions pour le PDF
    const translations = {
      labels: {
        weight: t('pdf.labels.weight'),
        height: t('pdf.labels.height'),
        birthDate: t('pdf.labels.birthDate'),
        nationality: t('pdf.labels.nationality'),
        secondNationality: t('pdf.labels.secondNationality'),
        geneticOrigin: t('pdf.labels.geneticOrigin'),
        nationalTeam: t('pdf.labels.nationalTeam'),
        team: t('pdf.labels.team'),
        secondaryLeague: t('pdf.labels.secondaryLeague'),
        groups: t('pdf.labels.groups'),
        position: t('pdf.labels.position'),
        dominantHand: t('pdf.labels.dominantHand'),
        shootingRange: t('pdf.labels.shootingRange'),
        offensiveRole: t('pdf.labels.offensiveRole'),
        defensiveRole: t('pdf.labels.defensiveRole'),
        jeJiSkills: t('pdf.labels.jeJiSkills'),
        attributes: t('pdf.labels.attributes'),
        flagBehaviour: t('pdf.labels.flagBehaviour'),
        injuryProne: t('pdf.labels.injuryProne'),
        currentLevel: t('pdf.labels.currentLevel'),
        projection: t('pdf.labels.projection'),
        proComparison: t('pdf.labels.proComparison'),
        agency: t('pdf.labels.agency'),
        salaryRange: t('pdf.labels.salaryRange'),
        contract: t('pdf.labels.contract'),
        buyout: t('pdf.labels.buyout'),
        draftYear: t('pdf.labels.draftYear'),
        draftPosition: t('pdf.labels.draftPosition'),
        preDraftTeam: t('pdf.labels.preDraftTeam'),
        pit: t('pdf.labels.pit'),
        eurocamp: t('pdf.labels.eurocamp'),
        nbaExperience: t('pdf.labels.nbaExperience'),
        acbExperience: t('pdf.labels.acbExperience'),
        europeExperience: t('pdf.labels.europeExperience'),
        ncaaExperience: t('pdf.labels.ncaaExperience'),
        scouting: t('pdf.labels.scouting'),
        event: t('pdf.labels.event'),
        source: t('pdf.labels.source'),
        ranking: t('pdf.labels.ranking'),
        tracking: t('pdf.labels.tracking')
      },
      sections: {
        characteristics: t('pdf.sections.characteristics'),
        professionalExperience: t('pdf.sections.professionalExperience'),
        contractInfo: t('pdf.sections.contractInfo'),
        report: t('pdf.sections.report'),
        intel: t('pdf.sections.intel')
      },
      footer: {
        department: t('pdf.footer.department')
      },
      filename: {
        prefix: t('pdf.filename.prefix')
      }
    };

    await generatePDF({
      player,
      selectedImagePreview,
      avatarTimestamp,
      mediaUrl,
      backendUrl,
      section,
      includeProfileInfo,
      includeCaracteristicas,
      includeExperiencia,
      includeContrato,
      checkedInformes,
      checkedIntels,
      clubInfo,
      translations,
      translateValue
    });
  };

  const isScout = userRole === 'scout interne' || userRole === 'scout externe' || userRole === 'scout interne cantera' || userRole === 'scout externe cantera';
  const isScoutExterne = userRole === 'scout externe' || userRole === 'scout externe cantera';

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !player) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error || 'Error al cargar el jugador'}
      </Alert>
    );
  }

  if (isMobile) {
    const sections = [
      { id: 'info', name: t('playerProfile.tabs.profile'), icon: <EditIcon />, available: true },
      { id: 'tecnica', name: t('playerProfile.tabs.technicalSheet'), icon: <EditIcon />, available: hasTechnicaData },
      { id: 'informe', name: t('playerProfile.tabs.report'), icon: <EditIcon />, available: hasInformeData },
      { id: 'intel', name: t('playerProfile.tabs.intel'), icon: <EditIcon />, available: hasIntelData },
      { id: 'medical', name: t('playerProfile.tabs.medical'), icon: <EditIcon />, available: hasMedicalData },
      { id: 'medidas', name: t('playerProfile.tabs.measurements'), icon: <EditIcon />, available: hasMedidasData && !isScout },
      { id: 'documents', name: t('playerProfile.tabs.documents'), icon: <EditIcon />, available: hasDocumentsData },
      { id: 'video', name: t('playerProfile.tabs.videos'), icon: <EditIcon />, available: hasVideoData },
      { id: 'pdp', name: t('playerProfile.tabs.pdp'), icon: <EditIcon />, available: hasPDPData && !isScout },
      { id: 'news', name: t('playerProfile.tabs.news'), icon: <EditIcon />, available: hasNewsData && !isScout },
      { id: 'estadisticas', name: t('playerProfile.tabs.statistics'), icon: <EditIcon />, available: hasStats && !isScout },
      { id: 'schedule', name: t('playerProfile.tabs.schedule'), icon: <EditIcon />, available: hasSchedule && !isScout },
      { id: 'canterastats', name: t('playerProfile.tabs.statistics'), icon: <EditIcon />, available: hasCanteraStats && !isScout }
    ].filter((s) => s.available);

    const getCurrentSection = () => sections[activeTab]?.id || 'info';
    const currentSection = getCurrentSection();

    return (
      <Box sx={{ pb: 8, minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <MobileProfileHeader
          player={player}
          avatarUrl={selectedImagePreview || `${mediaUrl}/photos/${player.id}.png?${avatarTimestamp}`}
          isEditing={isEditing}
          onEditPhoto={() => setOpenPhotoDialog(true)}
          onBack={() => navigate(-1)}
          onCreateFichaClick={() => setOpenCreateFichaDialog(true)}
          isInjured={isInjured}
          latestMedical={latestMedical}
          navigate={navigate}
          wingspanDiff={wingspanDiff}
          winPercentage={winPercentage}
          acbExperience={acbExperience}
          shootingSummary={shootingSummary}
        />
        <Box sx={{ px: 2, py: 3 }}>
          {currentSection === 'info' && (
            <>
              {/* Affichage des Scout Verdicts en mobile */}
              <ScoutVerdicts playerId={player.id} />
              
              <InformacionBasica
                player={player}
                editablePlayer={editablePlayer}
                setEditablePlayer={setEditablePlayer}
                isEditing={isEditing}
                isMobile={true}
                allGroups={allGroups}
                handleChange={handleChange}
                handleGroupChange={handleGroupChange}
              />
              <CaracteristicasJugador
                player={player}
                editablePlayer={editablePlayer}
                setEditablePlayer={setEditablePlayer}
                isEditing={isEditing}
                isMobile={true}
                handleChange={handleChange}
              />
              <DefensiveVersatilitySection
                player={player}
                editablePlayer={editablePlayer}
                setEditablePlayer={setEditablePlayer}
                isEditing={isEditing}
                isMobile={true}
              />
              <SituacionActual
                player={player}
                editablePlayer={editablePlayer}
                setEditablePlayer={setEditablePlayer}
                isEditing={isEditing}
                isMobile={true}
                teams={teams}
                leagues2={leagues2}
              />
              <ExperienciaProfesional
                player={player}
                editablePlayer={editablePlayer}
                setEditablePlayer={setEditablePlayer}
                isEditing={isEditing}
                isMobile={true}
                teams={teams}
                handleChange={handleChange}
              />
              <InformacionContractual
                player={player}
                editablePlayer={editablePlayer}
                setEditablePlayer={setEditablePlayer}
                isEditing={isEditing}
                isMobile={true}
                agencies={agencies}
                priceRanges={priceRanges}
                contratos={contratos}
                buyoutOptions={buyoutOptions}
                userRole={userRole}
              />
              <InformacionAdicional
                player={player}
                editablePlayer={editablePlayer}
                setEditablePlayer={setEditablePlayer}
                isEditing={isEditing}
                isMobile={true}
                handleChange={handleChange}
              />
            </>
          )}

          {currentSection === 'tecnica' && (
            <TechnicaTab
              playerId={player.id}
              dateNaissance={player.dateNaissance}
              nom={player.nom}
              onDataLoaded={setHasTechnicaData}
            />
          )}

          {currentSection === 'informe' && (
            <InformeTab playerId={player.id} dateNaissance={player.date_naissance} onDataLoaded={setHasInformeData} />
          )}

          {currentSection === 'intel' && (
            <IntelTab playerId={player.id} onDataLoaded={setHasIntelData} />
          )}

          {currentSection === 'video' && (
            <VideoTab playerId={player.id} onDataLoaded={setHasVideoData} />
          )}

          {currentSection === 'pdp' && (
            <PDPTab playerId={player.id} onDataLoaded={setHasPDPData} />
          )}

          {currentSection === 'news' && (
            <NewsTab playerId={player.id} />
          )}

          {currentSection === 'estadisticas' && (
            <EstadisticasTab playerId={player.id} />
          )}

          {currentSection === 'schedule' && (
            <ProfilScheduleTab playerId={player.id} />
          )}

          {currentSection === 'medical' && (
            <MedicalTab
              playerId={player.id}
              onDataLoaded={setHasMedicalData}
              playerType={player.type}
              onRefreshLatestMedical={handleRefreshLatestMedical}
            />
          )}

          {currentSection === 'medidas' && (
           <MedidasTab playerId={player.id} dateNaissance={player.date_naissance} />
          )}

          {currentSection === 'documents' && (
            <DocumentsTab playerId={player.id} onDataLoaded={setHasDocumentsData} />
          )}

          {currentSection === 'canterastats' && (
            <CanteraStatsTab playerId={player.id} />
          )}
        </Box>

        <PhotoEditDialog
          open={openPhotoDialog}
          onClose={() => setOpenPhotoDialog(false)}
          onCapture={handlePhotoCapture}
          onSelect={handlePhotoSelect}
        />

        <MobileDrawerMenu
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sections={sections}
          activeSection={activeTab}
          onSectionChange={setActiveTab}
        />

        {sections.length > 1 && (
          <Box sx={{ position: 'fixed', bottom: 10, left: 0, right: 0, zIndex: theme.zIndex.appBar }}>
            <Paper elevation={3}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ backgroundColor: 'background.paper' }}
              >
                {sections.map((section) => (
                  <Tab key={section.id} label={section.name} />
                ))}
              </Tabs>
            </Paper>
          </Box>
        )}
        <AppBar
          position="fixed"
          color="primary"
          sx={{ top: 'auto', bottom: 58, backgroundColor: 'transparent', boxShadow: 'none' }}
        >
          <Toolbar>
            <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
              {currentSection === 'info' && !isScoutExterne && (
                <Button
                  variant="contained"
                  onClick={() => { isEditing ? handleUpdate() : setIsEditing(true); }}
                  fullWidth
                >
                  {isEditing ? t('common.save') : t('common.edit')}
                </Button>
              )}
              {!isEditing && currentSection === 'info' && !isScout && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleOpenPdfDialog}
                  fullWidth
                >
                  {t('playerProfile.buttons.generatePdf')}
                </Button>
              )}
            </Box>
          </Toolbar>
        </AppBar>

        <Dialog
          open={pdfDialogOpen}
          onClose={handleClosePdfDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{t('playerProfile.pdf.title')}</DialogTitle>
          <DialogContent>
            <Typography variant="subtitle1" gutterBottom>
              {t('playerProfile.pdf.selectSections')}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectAllSections}
                  onChange={(e) => {
                    setSelectAllSections(e.target.checked);
                    setIncludeProfileInfo(e.target.checked);
                    setIncludeCaracteristicas(e.target.checked);
                    setIncludeExperiencia(e.target.checked);
                    setIncludeContrato(e.target.checked);
                  }}
                />
              }
              label={t('playerProfile.pdf.selectAll')}
            />
            <FormGroup sx={{ ml: 2 }}>
              <FormControlLabel
                control={<Checkbox checked={includeProfileInfo} onChange={(e) => setIncludeProfileInfo(e.target.checked)} />}
                label={t('playerProfile.pdf.sections.basicInfo')}
              />
              <FormControlLabel
                control={<Checkbox checked={includeCaracteristicas} onChange={(e) => setIncludeCaracteristicas(e.target.checked)} />}
                label={t('playerProfile.pdf.sections.characteristics')}
              />
              <FormControlLabel
                control={<Checkbox checked={includeExperiencia} onChange={(e) => setIncludeExperiencia(e.target.checked)} />}
                label={t('playerProfile.pdf.sections.experience')}
              />
              <FormControlLabel
                control={<Checkbox checked={includeContrato} onChange={(e) => setIncludeContrato(e.target.checked)} />}
                label={t('playerProfile.pdf.sections.contract')}
              />
            </FormGroup>
            <Divider sx={{ my: 2 }} />
            {informeList.length > 0 && (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  {t('playerProfile.pdf.selectReports')}
                </Typography>
                <FormControlLabel
                  control={<Checkbox checked={masterCheckInformes} onChange={handleMasterCheckInformes} />}
                  label={t('playerProfile.pdf.selectAllReports')}
                />
                <FormGroup sx={{ ml: 2 }}>
                  {informeList.map((inf) => (
                    <FormControlLabel
                      key={inf.id}
                      control={
                        <Checkbox
                          checked={checkedInformes.some((item) => item.id === inf.id)}
                          onChange={() => handleToggleInforme(inf)}
                        />
                      }
                      label={`${new Date(inf.fecha).toLocaleDateString()} - ${inf.usuario_nombre} - ${inf.evento}`}
                    />
                  ))}
                </FormGroup>
                <Divider sx={{ my: 2 }} />
              </>
            )}
            {intelList.length > 0 && (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  {t('playerProfile.pdf.selectIntel')}
                </Typography>
                <FormControlLabel
                  control={<Checkbox checked={masterCheckIntels} onChange={handleMasterCheckIntels} />}
                  label={t('playerProfile.pdf.selectAllIntel')}
                />
                <FormGroup sx={{ ml: 2 }}>
                  {intelList.map((intel) => (
                    <FormControlLabel
                      key={intel.id}
                      control={
                        <Checkbox
                          checked={checkedIntels.some((item) => item.id === intel.id)}
                          onChange={() => handleToggleIntel(intel)}
                        />
                      }
                      label={`${new Date(intel.fecha).toLocaleDateString()} - ${intel.usuario_nombre} - ${intel.fuente}`}
                    />
                  ))}
                </FormGroup>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePdfDialog}>{t('common.cancel')}</Button>
            <Button
              variant="contained"
              onClick={handleGeneratePDF}
            >
              {t('playerProfile.buttons.generatePdf')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openCreateFichaDialog}
          onClose={() => setOpenCreateFichaDialog(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>{t('playerProfile.createCard.title')}</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('playerProfile.createCard.selectType')}
            </Typography>
            <TextField
              select
              label={t('playerProfile.createCard.cardType')}
              value={selectedFichaType}
              onChange={(e) => setSelectedFichaType(e.target.value)}
              fullWidth
              SelectProps={{ native: true }}
            >
              <option value="" />
              <option value="techniques">{t('playerProfile.createCard.types.technical')}</option>
              <option value="informe">{t('playerProfile.createCard.types.report')}</option>
              <option value="intel">{t('playerProfile.createCard.types.intel')}</option>
              <option value="video">{t('playerProfile.createCard.types.video')}</option>
              <option value="medical">{t('playerProfile.createCard.types.medical')}</option>
              <option value="medidas">{t('playerProfile.createCard.types.measurements')}</option>
              <option value="documents">{t('playerProfile.createCard.types.documents')}</option>
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCreateFichaDialog(false)}>{t('common.cancel')}</Button>
            <Button
              variant="contained"
              onClick={() => {
                if (!selectedFichaType) return;
                setOpenCreateFichaDialog(false);
                navigate(`/crear-ficha?playerId=${player.id}&cardType=${selectedFichaType}`);
              }}
            >
              {t('common.create')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  const sections = [
    { id: 'info', name: t('playerProfile.tabs.profile'), icon: <EditIcon />, available: true },
    { id: 'tecnica', name: t('playerProfile.tabs.technicalSheet'), icon: <EditIcon />, available: hasTechnicaData },
    { id: 'informe', name: t('playerProfile.tabs.report'), icon: <EditIcon />, available: hasInformeData },
    { id: 'intel', name: t('playerProfile.tabs.intel'), icon: <EditIcon />, available: hasIntelData },
    { id: 'medical', name: t('playerProfile.tabs.medical'), icon: <EditIcon />, available: hasMedicalData },
    { id: 'medidas', name: t('playerProfile.tabs.measurements'), icon: <EditIcon />, available: hasMedidasData && !isScout },
    { id: 'documents', name: t('playerProfile.tabs.documents'), icon: <EditIcon />, available: hasDocumentsData },
    { id: 'video', name: t('playerProfile.tabs.videos'), icon: <EditIcon />, available: hasVideoData },
    { id: 'instat', name: t('playerProfile.tabs.instat'), icon: <SportsBasketballIcon />, available: !!player.instat_player_id },
    { id: 'pdp', name: t('playerProfile.tabs.pdp'), icon: <EditIcon />, available: hasPDPData && !isScout },
    { id: 'news', name: t('playerProfile.tabs.news'), icon: <EditIcon />, available: hasNewsData && !isScout },
    { id: 'estadisticas', name: t('playerProfile.tabs.statistics'), icon: <EditIcon />, available: hasStats && !isScout },
    { id: 'schedule', name: t('playerProfile.tabs.schedule'), icon: <EditIcon />, available: hasSchedule && !isScout },
    { id: 'canterastats', name: t('playerProfile.tabs.statistics'), icon: <EditIcon />, available: hasCanteraStats && !isScout }
  ].filter((s) => s.available);

  const getCurrentSection = () => sections[activeTab]?.id || 'info';
  const currentSection = getCurrentSection();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', position: 'relative' }}>
      <Box
        sx={{
          width: leftPanelCollapsed ? 0 : 300,
          flexShrink: 0,
          pr: leftPanelCollapsed ? 0 : 2,
          transition: 'width 0.3s ease, padding 0.3s ease',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <Paper sx={{ p: 3, mb: 2, textAlign: 'center', position: 'relative' }}>
          {/* Bouton de rétraction du panneau latéral */}
          <IconButton
            onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
            sx={{
              position: 'absolute',
              right: -0,
              top: 0,
              zIndex: 10,
              backgroundColor: 'primary.main',
              color: 'white',
              width: 36,
              height: 36,
              borderRadius: '0 50% 50% 0',
              '&:hover': {
                backgroundColor: 'primary.dark',
                transform: 'scale(1.1)'
              },
              boxShadow: 3
            }}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>

          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Avatar
              src={selectedImagePreview || `${mediaUrl}/photos/${player.id}.png?${avatarTimestamp}`}
              alt={player.nom}
              sx={{ width: 200, height: 200, mx: 'auto', mb: 2 }}
              onClick={() => isEditing && setOpenPhotoDialog(true)}
            />
            {!isScoutExterne && (
              <IconButton
                aria-label="Crear Ficha"
                onClick={() => setOpenCreateFichaDialog(true)}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  backgroundColor: '#1976d2',
                  color: '#fff',
                  zIndex: 10,
                  width: 40,
                  height: 40,
                  '&:hover': {
                    backgroundColor: '#1565c0'
                  }
                }}
              >
                <AddIcon />
              </IconButton>
            )}

            {isInjured && (
              <Box sx={{
                position: 'absolute',
                top: '-20px',
                right: 0,
                zIndex: 10
              }}>
                <IconButton
                  aria-label="Blessure en cours"
                  sx={{
                    backgroundColor: 'red',
                    color: '#fff',
                    width: 40,
                    height: 40,
                    '&:hover': { backgroundColor: '#c62828' }
                  }}
                >
                  <HealingIcon />
                </IconButton>
                {latestMedical?.date_reprise && new Date(latestMedical.date_reprise) > new Date() && (
                  <Typography variant="caption" sx={{ color: 'black', backgroundColor: 'white', px: 1, borderRadius: 1 }}>
                    {new Date(latestMedical.date_reprise).toLocaleDateString('es-ES')}
                  </Typography>
                )}
              </Box>
            )}

            {(player.competitions_detail?.[0]?.tanteo_id || player.tanteo_id) && (
              <Tooltip title={`Derecho de tanteo: ${player.competitions_detail?.[0]?.tanteo_nom || player.tanteo_nom || ''}`}>
                <Box sx={{
                  position: 'absolute',
                  top: '-20px',
                  left: 48,
                  zIndex: 10
                }}>
                  <IconButton
                    aria-label="Derecho de tanteo"
                    sx={{
                      backgroundColor: '#f9a825',
                      color: '#fff',
                      width: 40,
                      height: 40,
                      '&:hover': { backgroundColor: '#f57f17' }
                    }}
                  >
                    <GavelIcon />
                  </IconButton>
                </Box>
              </Tooltip>
            )}
          </Box>

          <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            {winPercentage !== null && (
              <Tooltip title={t('playerProfile.winPercentage.tooltip', { wins: winPercentage.wins, games: winPercentage.totalGames })} arrow>
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 2,
                    fontWeight: 'bold',
                    color: winPercentage.winPercentage >= 50 ? '#4caf50' : '#f44336',
                    backgroundColor: winPercentage.winPercentage >= 50 ? '#e8f5e9' : '#ffebee',
                    px: 0.8,
                    py: 0.2,
                    borderRadius: 1,
                    fontSize: '0.75rem'
                  }}
                >
                  {winPercentage.winPercentage}% W
                </Typography>
              </Tooltip>
            )}
            {player?.nacionalidad && <FlagDisplay nationality={player.nacionalidad} size="32px" />}
            {wingspanDiff !== null && (
              <Tooltip title={t('playerProfile.wingspan.tooltip', { diff: `${wingspanDiff >= 0 ? '+' : ''}${wingspanDiff}` })} arrow>
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 2,
                    fontWeight: 'bold',
                    color: wingspanDiff > 0 ? '#4caf50' : wingspanDiff < 0 ? '#f44336' : '#9e9e9e',
                    backgroundColor: wingspanDiff > 0 ? '#e8f5e9' : wingspanDiff < 0 ? '#ffebee' : '#f5f5f5',
                    px: 0.8,
                    py: 0.2,
                    borderRadius: 1,
                    fontSize: '0.75rem'
                  }}
                >
                  {wingspanDiff > 0 ? '+' : ''}{wingspanDiff}cm
                </Typography>
              </Tooltip>
            )}
          </Box>

          <Typography variant="h6" gutterBottom>
            {player.nom}
          </Typography>

          {shootingSummary && <ShootingBadges data={shootingSummary} />}

          {acbExperience && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1, mb: 1 }}>
              <Tooltip title={t('playerProfile.acb.seasonsTooltip', { count: acbExperience.seasons })} arrow>
                <Chip
                  icon={<CalendarMonthIcon sx={{ color: '#000 !important', fontSize: '1rem !important' }} />}
                  label={acbExperience.seasons}
                  size="small"
                  sx={{ backgroundColor: '#FFD700', color: '#000', fontWeight: 'bold', height: 22, '& .MuiChip-label': { px: 0.75 } }}
                />
              </Tooltip>
              <Tooltip title={t('playerProfile.acb.gamesTooltip', { count: acbExperience.matches })} arrow>
                <Chip
                  icon={<SportsBasketballIcon sx={{ color: '#fff !important', fontSize: '1rem !important' }} />}
                  label={acbExperience.matches}
                  size="small"
                  sx={{ backgroundColor: '#1565c0', color: '#fff', fontWeight: 'bold', height: 22, '& .MuiChip-label': { px: 0.75 } }}
                />
              </Tooltip>
            </Box>
          )}

          <FormIndicator forme={player.forme_indicator} size={36} />
          
          <Box sx={{ mb: 2 }}>
            {player.type && (
              <Chip
                label={player.type === 'cantera' ? t('bigBoardFilters.cantera') : t('bigBoardFilters.pro')}
                sx={{
                  backgroundColor: player.type === 'cantera' ? '#FF8C00' : '#007FFF',
                  color: '#fff'
                }}
              />
            )}
          </Box>
          
          {player?.tags && player.tags.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', mb: 2 }}>
              {player.tags.map((tag) => (
                <Tooltip key={tag.id} title={tag.source === 'big_board' ? 'Ranking' : 'Seguimiento'} placement="top" arrow>
                  <Chip
                    label={tag.libelle}
                    onClick={() => {
                      // Navigue vers le bon onglet avec le tag étendu
                      if (tag.source === 'big_board') {
                        // Pour BigBoardTable via l'onglet Rankings
                        navigate(`/scouting?tab=ranking&expanded=${tag.id}&tagIds=${tag.id}`);
                      } else {
                        // Pour SeguimientoTable via l'onglet Seguimientos
                        navigate(`/scouting?tab=seguimiento&expanded=${tag.id}&tagIds=${tag.id}`);
                      }
                    }}
                    sx={{
                      backgroundColor: tag.source === 'big_board' ? '#1565c0' : '#c62828',
                      color: '#fff',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8,
                        transform: 'scale(1.05)',
                        transition: 'all 0.2s'
                      }
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          )}
          
          {player?.url && (
            <Button
              variant="outlined"
              startIcon={<OpenInNew />}
              href={player.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mb: 2 }}
              fullWidth
            >
              {t('playerProfile.buttons.statisticalSheet')}
            </Button>
          )}
          
          {currentSection === 'info' && !isScoutExterne && (
            <>
              <Button
                variant="contained"
                onClick={() => { isEditing ? handleUpdate() : setIsEditing(true); }}
                fullWidth
                sx={{ mb: 1 }}
              >
                {isEditing ? t('common.save') : t('common.edit')}
              </Button>
              {!isEditing && !isScout && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleOpenPdfDialog}
                  fullWidth
                >
                  {t('playerProfile.buttons.generatePdf')}
                </Button>
              )}
            </>
          )}
        </Paper>

        {sections.length > 1 && (
          <Paper sx={{ p: 1, mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              orientation="vertical"
              variant="scrollable"
            >
              {sections.map((sectionItem) => (
                <Tab key={sectionItem.id} label={sectionItem.name} />
              ))}
            </Tabs>
          </Paper>
        )}

        {/* Affichage des Scout Verdicts en desktop sous les tabs */}
        <ScoutVerdicts playerId={player.id} />
      </Box>

      {/* Bouton pour ouvrir le panneau quand il est rétracté */}
      {leftPanelCollapsed && (
        <IconButton
          onClick={() => setLeftPanelCollapsed(false)}
          sx={{
            position: 'fixed',
            left: 0,
            top: 80,
            zIndex: 1300,
            backgroundColor: 'primary.main',
            color: 'white',
            width: 36,
            height: 36,
            borderRadius: '0 50% 50% 0',
            '&:hover': {
              backgroundColor: 'primary.dark',
              transform: 'scale(1.1)'
            },
            boxShadow: 3
          }}
        >
          <ChevronRight fontSize="small" />
        </IconButton>
      )}

      <Box sx={{ flex: 1, minWidth: 0, p: 2 }}>
        {currentSection === 'info' && (
          <>
            <InformacionBasica
              player={player}
              editablePlayer={editablePlayer}
              setEditablePlayer={setEditablePlayer}
              isEditing={isEditing}
              isMobile={false}
              allGroups={allGroups}
              handleChange={handleChange}
              handleGroupChange={handleGroupChange}
            />
            <CaracteristicasJugador
              player={player}
              editablePlayer={editablePlayer}
              setEditablePlayer={setEditablePlayer}
              isEditing={isEditing}
              isMobile={false}
              handleChange={handleChange}
            />
            <DefensiveVersatilitySection
              player={player}
              editablePlayer={editablePlayer}
              setEditablePlayer={setEditablePlayer}
              isEditing={isEditing}
              isMobile={false}
            />
            <SituacionActual
              player={player}
              editablePlayer={editablePlayer}
              setEditablePlayer={setEditablePlayer}
              isEditing={isEditing}
              isMobile={false}
              teams={teams}
              leagues2={leagues2}
            />
            <ExperienciaProfesional
              player={player}
              editablePlayer={editablePlayer}
              setEditablePlayer={setEditablePlayer}
              isEditing={isEditing}
              isMobile={false}
              teams={teams}
              handleChange={handleChange}
            />
            <InformacionContractual
              player={player}
              editablePlayer={editablePlayer}
              setEditablePlayer={setEditablePlayer}
              isEditing={isEditing}
              isMobile={false}
              agencies={agencies}
              priceRanges={priceRanges}
              contratos={contratos}
              buyoutOptions={buyoutOptions}
              userRole={userRole}
            />
            <InformacionAdicional
              player={player}
              editablePlayer={editablePlayer}
              setEditablePlayer={setEditablePlayer}
              isEditing={isEditing}
              isMobile={false}
              handleChange={handleChange}
            />
          </>
        )}

        {currentSection === 'tecnica' && (
          <TechnicaTab
            playerId={player.id}
            dateNaissance={player.dateNaissance}
            nom={player.nom}
            onDataLoaded={setHasTechnicaData}
          />
        )}

        {currentSection === 'informe' && (
          <InformeTab playerId={player.id} dateNaissance={player.date_naissance} onDataLoaded={setHasInformeData} />
        )}

        {currentSection === 'intel' && (
          <IntelTab playerId={player.id} onDataLoaded={setHasIntelData} />
        )}

        {currentSection === 'video' && (
          <VideoTab playerId={player.id} onDataLoaded={setHasVideoData} />
        )}

        {currentSection === 'instat' && player.instat_player_id && (
          <InstatPlayerView
            instatPlayerId={player.instat_player_id}
            localPlayerId={player.id}
            embedded
            stickyTop={0}
          />
        )}

        {currentSection === 'pdp' && (
          <PDPTab playerId={player.id} onDataLoaded={setHasPDPData} />
        )}

        {currentSection === 'news' && (
          <NewsTab playerId={player.id} />
        )}

        {currentSection === 'estadisticas' && (
          <EstadisticasTab playerId={player.id} />
        )}

        {currentSection === 'schedule' && (
          <ProfilScheduleTab playerId={player.id} />
        )}

        {currentSection === 'medical' && (
          <MedicalTab
            playerId={player.id}
            onDataLoaded={setHasMedicalData}
            playerType={player.type}
            onRefreshLatestMedical={handleRefreshLatestMedical}
          />
        )}

        {currentSection === 'medidas' && (
          <MedidasTab playerId={player.id} dateNaissance={player.date_naissance} />
        )}

        {currentSection === 'documents' && (
          <DocumentsTab playerId={player.id} onDataLoaded={setHasDocumentsData} />
        )}

        {currentSection === 'canterastats' && (
          <CanteraStatsTab playerId={player.id} />
        )}
      </Box>

      <PhotoEditDialog
        open={openPhotoDialog}
        onClose={() => setOpenPhotoDialog(false)}
        onCapture={handlePhotoCapture}
        onSelect={handlePhotoSelect}
      />

      <Dialog
        open={pdfDialogOpen}
        onClose={handleClosePdfDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('playerProfile.pdf.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            {t('playerProfile.pdf.selectSections')}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectAllSections}
                onChange={(e) => {
                  setSelectAllSections(e.target.checked);
                  setIncludeProfileInfo(e.target.checked);
                  setIncludeCaracteristicas(e.target.checked);
                  setIncludeExperiencia(e.target.checked);
                  setIncludeContrato(e.target.checked);
                }}
              />
            }
            label={t('playerProfile.pdf.selectAll')}
          />
          <FormGroup sx={{ ml: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={includeProfileInfo} onChange={(e) => setIncludeProfileInfo(e.target.checked)} />}
              label={t('playerProfile.pdf.sections.basicInfo')}
            />
            <FormControlLabel
              control={<Checkbox checked={includeCaracteristicas} onChange={(e) => setIncludeCaracteristicas(e.target.checked)} />}
              label={t('playerProfile.pdf.sections.characteristics')}
            />
            <FormControlLabel
              control={<Checkbox checked={includeExperiencia} onChange={(e) => setIncludeExperiencia(e.target.checked)} />}
              label={t('playerProfile.pdf.sections.experience')}
            />
            <FormControlLabel
              control={<Checkbox checked={includeContrato} onChange={(e) => setIncludeContrato(e.target.checked)} />}
              label={t('playerProfile.pdf.sections.contract')}
            />
          </FormGroup>
          <Divider sx={{ my: 2 }} />
          {informeList.length > 0 && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {t('playerProfile.pdf.selectReports')}
              </Typography>
              <FormControlLabel
                control={<Checkbox checked={masterCheckInformes} onChange={handleMasterCheckInformes} />}
                label={t('playerProfile.pdf.selectAllReports')}
              />
              <FormGroup sx={{ ml: 2 }}>
                {informeList.map((inf) => (
                  <FormControlLabel
                    key={inf.id}
                    control={
                      <Checkbox
                        checked={checkedInformes.some((item) => item.id === inf.id)}
                        onChange={() => handleToggleInforme(inf)}
                      />
                    }
                    label={`${new Date(inf.fecha).toLocaleDateString()} - ${inf.usuario_nombre} - ${inf.evento}`}
                  />
                ))}
              </FormGroup>
              <Divider sx={{ my: 2 }} />
            </>
          )}
          {intelList.length > 0 && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {t('playerProfile.pdf.selectIntel')}
              </Typography>
              <FormControlLabel
                control={<Checkbox checked={masterCheckIntels} onChange={handleMasterCheckIntels} />}
                label={t('playerProfile.pdf.selectAllIntel')}
              />
              <FormGroup sx={{ ml: 2 }}>
                {intelList.map((intel) => (
                  <FormControlLabel
                    key={intel.id}
                    control={
                      <Checkbox
                        checked={checkedIntels.some((item) => item.id === intel.id)}
                        onChange={() => handleToggleIntel(intel)}
                        />
                      }
                      label={`${new Date(intel.fecha).toLocaleDateString()} - ${intel.usuario_nombre} - ${intel.fuente}`}
                    />
                  ))}
              </FormGroup>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePdfDialog}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleGeneratePDF}
          >
            {t('playerProfile.buttons.generatePdf')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCreateFichaDialog}
        onClose={() => setOpenCreateFichaDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('playerProfile.createCard.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('playerProfile.createCard.selectType')}
          </Typography>
          <TextField
            select
            label={t('playerProfile.createCard.cardType')}
            value={selectedFichaType}
            onChange={(e) => setSelectedFichaType(e.target.value)}
            fullWidth
            SelectProps={{ native: true }}
          >
            <option value="" />
            <option value="techniques">{t('playerProfile.createCard.types.technical')}</option>
            <option value="informe">{t('playerProfile.createCard.types.report')}</option>
            <option value="intel">{t('playerProfile.createCard.types.intel')}</option>
            <option value="video">{t('playerProfile.createCard.types.video')}</option>
            <option value="medical">{t('playerProfile.createCard.types.medical')}</option>
            <option value="medidas">{t('playerProfile.createCard.types.measurements')}</option>
            <option value="documents">{t('playerProfile.createCard.types.documents')}</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateFichaDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!selectedFichaType) return;
              setOpenCreateFichaDialog(false);
              navigate(`/crear-ficha?playerId=${player.id}&cardType=${selectedFichaType}`);
            }}
          >
            {t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const PhotoCapture = ({ onCapture }) => {
  const handlePhotoCapture = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const resized = await resizeImageFile(file);
        onCapture(resized);
      } catch (err) {
        console.error('Error resizing captured photo', err);
      }
    }
  };
  return (
    <Box>
      <input
        accept="image/*"
        id="photo-capture"
        type="file"
        capture="environment"
        onChange={handlePhotoCapture}
        style={{ display: 'none' }}
      />
      <label htmlFor="photo-capture">
        <Button variant="contained" component="span" startIcon={<PhotoCamera />} fullWidth>
          Tomar foto
        </Button>
      </label>
    </Box>
  );
};

const PhotoSelection = ({ onSelect }) => {
  const handlePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const resized = await resizeImageFile(file);
        onSelect(resized);
      } catch (err) {
        console.error('Error resizing selected photo', err);
      }
    }
  };
  return (
    <Box>
      <input
        accept="image/*"
        id="photo-select"
        type="file"
        onChange={handlePhotoSelect}
        style={{ display: 'none' }}
      />
      <label htmlFor="photo-select">
        <Button variant="contained" component="span" startIcon={<ImageIcon />} fullWidth>
          Seleccionar foto
        </Button>
      </label>
    </Box>
  );
};

const MobileDrawerMenu = ({ open, onClose, sections, activeSection, onSectionChange }) => {
  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{ '& .MuiDrawer-paper': { width: '80%', maxWidth: 360 } }}
    >
      <Box sx={{ width: '100%' }}>
        <List>
          <ListItem>
            <Typography variant="h6">Secciones</Typography>
          </ListItem>
          <Divider />
          {sections.map((section, index) => (
            <ListItem key={section.name} disablePadding>
              <ListItemButton
                selected={activeSection === index}
                onClick={() => {
                  onSectionChange(index);
                  onClose();
                }}
              >
                <ListItemIcon>{section.icon}</ListItemIcon>
                <ListItemText primary={section.name} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Drawer>
  );
};

const MobileProfileHeader = ({ player, avatarUrl, isEditing, onEditPhoto, onBack, onCreateFichaClick, isInjured, latestMedical, navigate, wingspanDiff, winPercentage, acbExperience, shootingSummary }) => {
  const { t } = useTranslation();
  return (
    <AppBar position="static" color="default" elevation={0}>
      <Toolbar>
        <IconButton edge="start" onClick={onBack}>
          <ArrowBack />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            py: 2,
            position: 'relative'
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={avatarUrl}
              alt={player?.nom}
              sx={{ width: 120, height: 120, border: '3px solid white', boxShadow: 1 }}
            />
            {isEditing && (
              <IconButton
                sx={{
                  position: 'absolute',
                  bottom: -10,
                  right: -10,
                  backgroundColor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': { backgroundColor: 'background.paper' }
                }}
                onClick={onEditPhoto}
              >
                <Edit />
              </IconButton>
            )}
            <IconButton
              aria-label="Crear Ficha"
              onClick={onCreateFichaClick}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                backgroundColor: '#1976d2',
                color: '#fff',
                zIndex: 10,
                width: 34,
                height: 34,
                '&:hover': {
                  backgroundColor: '#1565c0'
                }
              }}
            >
              <AddIcon sx={{ fontSize: 20 }} />
            </IconButton>

            {isInjured && (
              <Box sx={{
                position: 'absolute',
                top: '-30px',
                right: 0,
                zIndex: 10
              }}>
                <IconButton
                  aria-label="Blessure en cours"
                  sx={{
                    backgroundColor: 'red',
                    color: '#fff',
                    width: 34,
                    height: 34,
                    '&:hover': {
                      backgroundColor: '#c62828'
                    }
                  }}
                >
                  <HealingIcon sx={{ fontSize: 20 }} />
                </IconButton>
                {latestMedical?.date_reprise && new Date(latestMedical.date_reprise) > new Date() && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'black',
                      backgroundColor: 'white',
                      px: 0.5,
                      borderRadius: 1,
                      display: 'block'
                    }}
                  >
                    {new Date(latestMedical.date_reprise).toLocaleDateString('es-ES')}
                  </Typography>
                )}
              </Box>
            )}

            {(player.competitions_detail?.[0]?.tanteo_id || player.tanteo_id) && (
              <Tooltip title={`Derecho de tanteo: ${player.competitions_detail?.[0]?.tanteo_nom || player.tanteo_nom || ''}`}>
                <Box sx={{
                  position: 'absolute',
                  top: '-30px',
                  left: 42,
                  zIndex: 10
                }}>
                  <IconButton
                    aria-label="Derecho de tanteo"
                    sx={{
                      backgroundColor: '#f9a825',
                      color: '#fff',
                      width: 34,
                      height: 34,
                      '&:hover': { backgroundColor: '#f57f17' }
                    }}
                  >
                    <GavelIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              </Tooltip>
            )}
          </Box>

          <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            {winPercentage !== null && (
              <Tooltip title={t('playerProfile.winPercentage.tooltip', { wins: winPercentage.wins, games: winPercentage.totalGames })} arrow>
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 2,
                    fontWeight: 'bold',
                    color: winPercentage.winPercentage >= 50 ? '#4caf50' : '#f44336',
                    backgroundColor: winPercentage.winPercentage >= 50 ? '#e8f5e9' : '#ffebee',
                    px: 0.8,
                    py: 0.2,
                    borderRadius: 1,
                    fontSize: '0.7rem'
                  }}
                >
                  {winPercentage.winPercentage}% W
                </Typography>
              </Tooltip>
            )}
            {player?.nacionalidad && <FlagDisplay nationality={player.nacionalidad} size="28px" />}
            {wingspanDiff !== null && wingspanDiff !== undefined && (
              <Tooltip title={t('playerProfile.wingspan.tooltip', { diff: `${wingspanDiff >= 0 ? '+' : ''}${wingspanDiff}` })} arrow>
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 2,
                    fontWeight: 'bold',
                    color: wingspanDiff > 0 ? '#4caf50' : wingspanDiff < 0 ? '#f44336' : '#9e9e9e',
                    backgroundColor: wingspanDiff > 0 ? '#e8f5e9' : wingspanDiff < 0 ? '#ffebee' : '#f5f5f5',
                    px: 0.8,
                    py: 0.2,
                    borderRadius: 1,
                    fontSize: '0.7rem'
                  }}
                >
                  {wingspanDiff > 0 ? '+' : ''}{wingspanDiff}cm
                </Typography>
              </Tooltip>
            )}
          </Box>

          <Typography variant="h6" sx={{ mt: 2 }}>
            {player?.nom}
          </Typography>

          {shootingSummary && <ShootingBadges data={shootingSummary} />}

          {acbExperience && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1, mb: 1 }}>
              <Tooltip title={t('playerProfile.acb.seasonsTooltip', { count: acbExperience.seasons })} arrow>
                <Chip
                  icon={<CalendarMonthIcon sx={{ color: '#000 !important', fontSize: '1rem !important' }} />}
                  label={acbExperience.seasons}
                  size="small"
                  sx={{ backgroundColor: '#FFD700', color: '#000', fontWeight: 'bold', height: 22, '& .MuiChip-label': { px: 0.75 } }}
                />
              </Tooltip>
              <Tooltip title={t('playerProfile.acb.gamesTooltip', { count: acbExperience.matches })} arrow>
                <Chip
                  icon={<SportsBasketballIcon sx={{ color: '#fff !important', fontSize: '1rem !important' }} />}
                  label={acbExperience.matches}
                  size="small"
                  sx={{ backgroundColor: '#1565c0', color: '#fff', fontWeight: 'bold', height: 22, '& .MuiChip-label': { px: 0.75 } }}
                />
              </Tooltip>
            </Box>
          )}

          <FormIndicator forme={player.forme_indicator} size={32} />

          {player?.type && (
            <Chip
              label={player.type === 'cantera' ? t('bigBoardFilters.cantera') : t('bigBoardFilters.pro')}
              sx={{
                backgroundColor: player.type === 'cantera' ? '#FF8C00' : '#007FFF',
                color: '#fff',
                mt: 1
              }}
            />
          )}
          {player?.url && (
            <Button
              variant="outlined"
              startIcon={<OpenInNew />}
              href={player.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mt: 1, mb: 2, backgroundColor: 'white' }}
            >
              {t('playerProfile.buttons.statisticalSheet')}
            </Button>
          )}
          {player?.tags && player.tags.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', mb: 2 }}>
              {player.tags.map((tag) => (
                <Tooltip key={tag.id} title={tag.source === 'big_board' ? 'Ranking' : 'Seguimiento'} placement="top" arrow>
                  <Chip
                    label={tag.libelle}
                    onClick={() => {
                      // Navigue vers le bon onglet avec le tag étendu
                      if (tag.source === 'big_board') {
                        // Pour BigBoardTable via l'onglet Rankings
                        navigate(`/scouting?tab=ranking&expanded=${tag.id}&tagIds=${tag.id}`);
                      } else {
                        // Pour SeguimientoTable via l'onglet Seguimientos
                        navigate(`/scouting?tab=seguimiento&expanded=${tag.id}&tagIds=${tag.id}`);
                      }
                    }}
                    sx={{
                      backgroundColor: tag.source === 'big_board' ? '#1565c0' : '#c62828',
                      color: '#fff',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8,
                        transform: 'scale(1.05)',
                        transition: 'all 0.2s'
                      }
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          )}
          {player?.groupes && player.groupes.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', mt: 1 }}>
              {player.groupes.map((group) => (
                <Chip
                  key={group.id}
                  label={group.libelle}
                  size="small"
                  sx={{
                    backgroundColor: group.code_couleur || 'grey',
                    color: '#fff'
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

const PhotoEditDialog = ({ open, onClose, onCapture, onSelect }) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Cambiar foto</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <PhotoCapture
            onCapture={(file) => {
              onCapture(file);
              onClose();
            }}
          />
          <PhotoSelection
            onSelect={(file) => {
              onSelect(file);
              onClose();
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlayerProfile;